import { WebStorageStateStore } from "oidc-client-ts";
import type { AuthProviderProps } from "react-oidc-context";

/**
 * OIDC client settings consumed by `<AuthProvider>` from react-oidc-context.
 *
 * Local dev → Keycloak (`http://localhost:8081/realms/a2a`)
 * Production → AWS Cognito (`https://cognito-idp.<region>.amazonaws.com/<pool>`)
 *
 * Both are OIDC, so the only thing that swaps between environments is the
 * `authority` URL. The `client_id` is a public PKCE client in both worlds.
 *
 * Decisions worth knowing:
 *
 * - **PKCE-with-code is the only flow we use.** No implicit, no client
 *   secret. The library defaults to `response_type=code` + `code_challenge`
 *   when `client_secret` is absent, so we don't need to set it explicitly.
 *
 * - **`localStorage` for tokens.** Shared across tabs (and survives a tab
 *   close), so opening an authenticated route in a NEW TAB — e.g. an admin
 *   opening a user's read-only profile at `/admin/users/:id` — finds the
 *   session instead of bouncing to sign-in. `sessionStorage` is per-tab and
 *   would leave every new tab anonymous. Tradeoff vs sessionStorage: tokens
 *   live longer and are shared across tabs, a modestly larger XSS theft
 *   window — acceptable here, and the common choice for multi-tab SPAs. v2
 *   may move to in-memory + silent renewal once we have a longer-lived
 *   refresh story.
 *
 * - **Refresh-token silent renewal.** Iframe silent renewal is dead in
 *   modern browsers (third-party cookies). Keycloak issues refresh tokens
 *   to public PKCE clients by default, and Cognito does too. The library
 *   exchanges the refresh token a few minutes before access-token expiry.
 *
 * - **No dedicated `/callback` route.** redirect_uri is the SPA's root.
 *   On load, react-oidc-context inspects `window.location.search` for
 *   `code` + `state` params and processes them, then strips them from
 *   the URL via `history.replaceState`. Saves us from adding a router.
 */
/**
 * Where signing OUT lands the user: the public marketing site at the apex
 * (delgentic.com), NOT this app origin.
 *
 * <p>Necessary because {@link AuthGate} now sends anonymous visitors straight
 * to the IdP — returning a signed-out user to this origin would bounce them
 * right back to a login page instead of letting them land somewhere signed-out.
 * Sending them to the apex matches the usual shape (sign out of Gmail → land on
 * google.com).</p>
 *
 * <p>Falls back to this origin when unset, which keeps local dev (and any
 * environment with no separate marketing site) behaving exactly as before.
 * The value must also be registered in the app client's allowed sign-out URLs —
 * IdentityStack adds the catalog origins for exactly this reason.</p>
 */
export const SITE_URL =
  (import.meta.env.VITE_SITE_URL as string | undefined) ??
  window.location.origin + "/";

/**
 * Marks this load as a fresh login so {@code CalendarLoginSync} re-pulls the
 * user's Google calendar (features/profile/CalendarLoginSync.tsx reads + clears
 * this). It is the one signal that distinguishes a real sign-in from a plain
 * reload, so BOTH sign-in paths have to raise it — the redirect flow from
 * {@code onSigninCallback} below, and the native pages via the session facade.
 */
export function markFreshLogin(): void {
  try {
    window.sessionStorage.setItem("a2a.calendarReimportPending", "1");
  } catch {
    // sessionStorage unavailable (private-mode edge cases) — non-fatal.
  }
}

export const oidcConfig: AuthProviderProps = {
  authority: import.meta.env.VITE_OIDC_AUTHORITY,
  client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
  redirect_uri: window.location.origin + "/",
  post_logout_redirect_uri: SITE_URL,

  // `openid` is required so userinfo + ID-token claims are populated.
  // `email` + `profile` give us the display name on the SignedInHeader.
  //
  // We previously also requested `offline_access`. Removed: Cognito returns
  // `invalid_scope` for it (it isn't on the App Client's allowed-scope list
  // and Cognito doesn't accept silently-unknown scopes the way some IdPs
  // do). Keycloak's default public-PKCE client config issues a refresh token
  // for `openid` alone, so dropping `offline_access` doesn't break local dev
  // either. If a future Keycloak realm change disables refresh tokens by
  // default, branch on `import.meta.env.VITE_OIDC_AUTHORITY` to add the
  // scope only for Keycloak.
  scope: "openid email profile",

  // Auto-redirect handling on first load.
  onSigninCallback: () => {
    // Fires exactly once after a login redirect — see markFreshLogin.
    markFreshLogin();
    // Strip ?code=...&state=... from the URL after a successful login so
    // refresh / share-link doesn't re-trigger the callback.
    window.history.replaceState({}, document.title, window.location.pathname);
  },

  // Token persistence. localStorage (not sessionStorage) so the session is
  // shared across tabs — opening an authed route in a new tab stays signed in.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  stateStore: new WebStorageStateStore({ store: window.localStorage }),

  // Silent renewal of access tokens via refresh_token grant. The library
  // schedules a renewal ~60s before expiry by default.
  automaticSilentRenew: true,

  // When the user signs out via the app, also revoke tokens server-side.
  revokeTokensOnSignout: true,

  // Revoke ONLY the refresh token. The library's default also revokes the
  // access token, but Cognito's /oauth2/revoke endpoint accepts refresh
  // tokens exclusively — the access-token call came back 400
  // `unsupported_token_type`, signoutRedirect() threw before redirecting,
  // and AuthGate showed "Sign-in failed" on every sign-out. Revoking the
  // refresh token is sufficient: Cognito invalidates the access/ID tokens
  // issued alongside it.
  revokeTokenTypes: ["refresh_token"],
};

/**
 * Sign out and bounce through the IdP so the server-side session dies too
 * (otherwise the next sign-in silently re-authenticates without a password).
 *
 * <p>Cognito's /logout endpoint is not OIDC RP-initiated-logout compliant:
 * it wants {@code client_id} + {@code logout_uri} (which must be on the App
 * Client's allowed sign-out URLs — IdentityStack registers the SPA origins)
 * rather than {@code post_logout_redirect_uri} + {@code id_token_hint}.
 * Passing both shapes keeps Keycloak (dev) and Cognito (prod) happy: each
 * reads the params it knows and ignores the rest.</p>
 */
export function signOutRedirect(auth: {
  signoutRedirect: (args?: {
    extraQueryParams?: Record<string, string>;
  }) => Promise<void>;
}): Promise<void> {
  return auth.signoutRedirect({
    extraQueryParams: {
      client_id: import.meta.env.VITE_OIDC_CLIENT_ID as string,
      logout_uri: SITE_URL,
    },
  });
}
